import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { DatePipe, NgClass, NgFor, NgIf } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
    ViewContainerRef,
    ViewEncapsulation,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterLink } from '@angular/router';
import { UpdateRequiredOrder } from 'app/layout/common/updates-required/updates-required.types';
import { UpdatesRequiredService } from 'app/layout/common/updates-required/updates-required.service';
import { OrdersService } from 'app/modules/admin/orders/orders.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'updates-required',
    templateUrl: './updates-required.component.html',
    styleUrls: ['./updates-required.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'updatesRequired',
    imports: [
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        DatePipe,
    ],
    standalone: true,
})
export class UpdatesRequiredComponent implements OnInit, OnDestroy {
    @ViewChild('updatesRequiredOrigin') private _updatesRequiredOrigin: any;
    @ViewChild('updatesRequiredPanel')
    private _updatesRequiredPanel: TemplateRef<any>;

    issueOrders: UpdateRequiredOrder[] = [];
    issueOrdersCount: number = 0;
    currentSlideIndex: number = 0;
    
    private _overlayRef: OverlayRef;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    private _refreshInterval: any; // For auto-refresh interval
    private _refreshIntervalTime: number = 60000; // 60 seconds refresh interval

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _updatesRequiredService: UpdatesRequiredService,
        private _overlay: Overlay,
        private _viewContainerRef: ViewContainerRef,
        private _router: Router,
        private _ordersService: OrdersService
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to issue orders changes
        this._updatesRequiredService.issueOrders$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((orders: UpdateRequiredOrder[]) => {
                // Load the issue orders
                this.issueOrders = orders;
                
                // Set the first slide as active if available
                if (orders.length > 0) {
                    this.showSlide(this._updatesRequiredService.currentSlideIndex);
                }

                // Mark for check
                this._changeDetectorRef.markForCheck();
            });
            
        // Subscribe to issue orders count changes
        this._updatesRequiredService.issueOrdersCount$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((count: number) => {
                this.issueOrdersCount = count;
                
                // Mark for check
                this._changeDetectorRef.markForCheck();
            });
            
        // Load issue orders from the real API 
        this._updatesRequiredService.getUpdateIssueOrders().subscribe(
            () => {
                // Successfully loaded data
                this._changeDetectorRef.markForCheck();
            },
            error => {
                console.error('Error loading updates required data:', error);
                // Show empty state by setting issue orders to empty array
                this.issueOrders = [];
                this.issueOrdersCount = 0;
                this._changeDetectorRef.markForCheck();
            }
        );
        
        // Start auto-refresh for updates required
        this.startAutoRefresh();
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();

        // Dispose the overlay
        if (this._overlayRef) {
            this._overlayRef.dispose();
        }
        
        // Stop auto-refresh
        this.stopAutoRefresh();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Open the panel
     */
    openPanel(): void {
        // Return if the panel or its origin is not defined
        if (!this._updatesRequiredPanel || !this._updatesRequiredOrigin) {
            return;
        }

        // Create the overlay if it doesn't exist
        if (!this._overlayRef) {
            this._createOverlay();
        }

        // Attach the portal to the overlay
        this._overlayRef.attach(
            new TemplatePortal(this._updatesRequiredPanel, this._viewContainerRef)
        );
    }

    /**
     * Close the panel
     */
    closePanel(): void {
        this._overlayRef.detach();
    }

    /**
     * Show slide at specific index
     */
    showSlide(index: number): void {
        this.currentSlideIndex = index;
        
        // Update service
        this._updatesRequiredService.currentSlideIndex = index;
        
        // Mark for check
        this._changeDetectorRef.markForCheck();
    }
    
    /**
     * Show next slide
     */
    nextSlide(): void {
        this._updatesRequiredService.nextSlide();
        this.showSlide(this._updatesRequiredService.currentSlideIndex);
    }
    
    /**
     * Show previous slide
     */
    prevSlide(): void {
        this._updatesRequiredService.prevSlide();
        this.showSlide(this._updatesRequiredService.currentSlideIndex);
    }
    
    /**
     * Get CSS class for order status dot
     */
    getStatusClass(status: string): string {
        switch (status.toLowerCase()) {
            case 'completed':
                return 'bg-green-500'; // Success
            case 'processing':
                return 'bg-amber-500'; // Warning
            case 'production':
                return 'bg-blue-500';  // Info
            case 'shipping':
                return 'bg-purple-500'; // Primary
            default:
                return 'bg-gray-500';
        }
    }
    
    /**
     * Get appropriate icon for issue type based on issue text
     */
    getIssueIcon(issueText: string): string {
        if (!issueText) {
            return 'heroicons_outline:exclamation-triangle';
        }
        
        // Map issue text to specific icons
        if (issueText.includes('Due Date approaching')) {
            return 'heroicons_outline:clock';
        } else if (issueText.includes('Due Date has passed')) {
            return 'heroicons_outline:calendar-days';
        } else if (issueText.includes('Order Marked Incomplete')) {
            return 'heroicons_outline:clipboard-document-check';
        } else if (issueText.includes('Missing')) {
            return 'heroicons_outline:document-minus';
        } else if (issueText.includes('Payment')) {
            return 'heroicons_outline:currency-dollar';
        } else if (issueText.includes('Film')) {
            return 'heroicons_outline:film';
        } else if (issueText.includes('Art')) {
            return 'heroicons_outline:photo';
        } else if (issueText.includes('Apparel')) {
            return 'heroicons_outline:shopping-bag';
        }
        
        // Default icon
        return 'heroicons_outline:exclamation-triangle';
    }
    
    /**
     * Get color class for icon based on issue text
     * This matches the style of the Timeline Activities panel
     */
    getIssueStatusColorClass(issueText: string): string {
        if (!issueText) {
            return 'text-gray-500';
        }
        
        // For most issue types, use red
        if (issueText.includes('Due Date has passed') || 
            issueText.includes('Order Marked Incomplete') ||
            issueText.includes('Missing')) {
            return 'text-red-500';
        } else if (issueText.includes('Due Date approaching')) {
            return 'text-amber-500'; // Warning color for approaching dates
        } else if (issueText.includes('Payment')) {
            return 'text-green-500'; // For payment related issues
        } else if (issueText.includes('Film') || issueText.includes('Art')) {
            return 'text-blue-500'; // For creative content
        } else if (issueText.includes('Apparel')) {
            return 'text-purple-500'; // For apparel related issues
        }
        
        // Default color
        return 'text-red-500';
    }
    
    /**
     * Start auto-refresh for updates required
     * This periodically refreshes the data to show new issues
     */
    startAutoRefresh(): void {
        // Clear any existing refresh interval first
        this.stopAutoRefresh();
        
        // Set up a new refresh interval
        this._refreshInterval = setInterval(() => {
            this._updatesRequiredService.getUpdateIssueOrders().subscribe();
        }, this._refreshIntervalTime);
    }
    
    /**
     * Stop auto-refresh for updates required
     */
    stopAutoRefresh(): void {
        // Clear the refresh interval if it exists
        if (this._refreshInterval) {
            clearInterval(this._refreshInterval);
            this._refreshInterval = null;
        }
    }
    
    /**
     * Open order for editing
     * Navigates to orders list with order ID as a query parameter
     * 
     * @param order The order to be edited
     */
    openOrderForEditing(order: UpdateRequiredOrder): void {
        // Close the panel first
        this.closePanel();
        
        // Navigate to the orders list page with the order ID as a query parameter
        this._router.navigate(['/admin/orders/list'], {
            queryParams: { editOrderId: order.id }
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Create the overlay
     */
    private _createOverlay(): void {
        // Create the overlay
        this._overlayRef = this._overlay.create({
            hasBackdrop: true,
            backdropClass: 'fuse-backdrop-on-mobile',
            scrollStrategy: this._overlay.scrollStrategies.block(),
            positionStrategy: this._overlay
                .position()
                .flexibleConnectedTo(
                    this._updatesRequiredOrigin._elementRef.nativeElement
                )
                .withLockedPosition(true)
                .withPush(true)
                .withPositions([
                    {
                        originX: 'center',
                        originY: 'bottom',
                        overlayX: 'center',
                        overlayY: 'top',
                    },
                    {
                        originX: 'center',
                        originY: 'top',
                        overlayX: 'center',
                        overlayY: 'bottom',
                    },
                    {
                        originX: 'start',
                        originY: 'bottom',
                        overlayX: 'start',
                        overlayY: 'top',
                    },
                    {
                        originX: 'end',
                        originY: 'bottom',
                        overlayX: 'end',
                        overlayY: 'top',
                    },
                ]),
        });

        // Detach the overlay from the portal on backdrop click
        this._overlayRef.backdropClick().subscribe(() => {
            this._overlayRef.detach();
        });
    }
}